import styled from '@emotion/styled';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { TableComponent, Typography } from '@innovamat/glimmer-components';
import type { Classroom } from '@innovamat/glow-api-client';
import { useInterventionReportQuery } from '@innovamat/glow-api-client';

import { useFlag } from '@innovamat/flags';
import { useUser } from '../../../user-management';
import ReportsLoader from '../../components/reports-loader';
import { CatchUpChart } from './catch-up-chart';
import { catchUpFakeData } from './catch-up-fake-data';
import { useScrollToCurrentWeek } from './hooks/use-scroll-to-current-week';
import { getCatchUpColumns } from './utils/get-catch-up-columns';
import { getStudentsRowsCatchUp } from './utils/preprocess';

type Row = {
  [key: string]: any;
};

type Props = {
  classroom: Classroom;
  setEventData: (eventType: string, eventProperties?: any) => void;
  useMockData?: boolean;
};

const FlexContainer = styled.div`
  display: flex;
`;

const InterventionCompletedTitle = styled(Typography.H4)`
  margin-top: 40px;
`;

export const TableContainer = styled.div`
  display: grid;
`;

export const CatchUpDashboard = ({
  classroom,
  setEventData,
  useMockData,
}: Props): JSX.Element => {
  const [classroomId, setClassroomId] = useState('');
  const { t } = useTranslation();
  const { user } = useUser();

  const { value: catchUpGraphicEnabled } = useFlag(
    'catchUpGraphicEnabled',
    false,
    {
      identifier: '',
      email: user?.email ?? '',
      custom: {},
    }
  );

  useEffect(() => {
    const appContent = document.getElementById('app-content');
    if (appContent) {
      appContent.style.overflow = 'auto';
      document.body.style.overflow = 'auto';
    }
    return () => {
      if (appContent) {
        appContent.style.overflow = '';
        document.body.style.overflow = 'scroll';
      }
    };
  }, []);

  useEffect(() => {
    if (classroom?.id) {
      setClassroomId(classroom.id);
    }
  }, [classroom]);

  const handleSendEvent = (event: string, parameters?: {}): void => {
    setEventData(event, {
      classroom_id: classroomId,
      ...parameters,
    });
  };

  useEffect(() => {
    handleSendEvent('catchup_dashboard_access');
  }, []);

  const { data, isLoading } = useInterventionReportQuery({
    id: classroomId,
  });

  useScrollToCurrentWeek(
    `current-week-${data && data.interventionReport?.weeks.currentWeekNumber}`,
    'catch-up-dashboard-table'
  );

  const interventionReport = useMockData
    ? catchUpFakeData.interventionReport
    : data?.interventionReport;

  if (isLoading) return <ReportsLoader />;
  if (!interventionReport) return <FlexContainer>No data</FlexContainer>; //TODO: Change this copy

  const interventionWeeks = interventionReport.weeks;

  const studentsRows = getStudentsRowsCatchUp(
    interventionReport.studentsCurrentlyInIntervention
  );

  const studentsNoLongerInInterventionRows = getStudentsRowsCatchUp(
    interventionReport.studentsNoLongerInIntervention
  );

  const getStudentsWithCompletedSessions = (
    studentsNoLongerInInterventionRows: Row[]
  ): Row[] => {
    return studentsNoLongerInInterventionRows.filter((student) =>
      student?.weeks?.some(
        (week: Row) => week.completedInterventionSessions > 0
      )
    );
  };

  const studentsWithCompletedSessions = getStudentsWithCompletedSessions(
    studentsNoLongerInInterventionRows
  );

  const columns = getCatchUpColumns({
    t,
    interventionWeeks,
    rows: studentsRows,
  });

  const columnsForDeactivated = getCatchUpColumns({
    t,
    interventionWeeks,
    rows: studentsWithCompletedSessions,
  });

  const numberOfWeeks = interventionWeeks.interventionWeeks.length;
  const percentiles = interventionReport.fluencyProgress?.percentileThresholds;
  const allStudents = [
    ...interventionReport.studentsCurrentlyInIntervention,
    ...interventionReport.studentsNoLongerInIntervention,
  ];

  return (
    <>
      {catchUpGraphicEnabled && (
        <CatchUpChart
          students={allStudents}
          percentiles={percentiles}
          maxWeeks={numberOfWeeks}
          currentWeek={interventionWeeks.currentWeekNumber}
        />
      )}
      <TableContainer>
        <TableComponent
          id={'INTRE_TABLE'}
          columns={columns}
          rows={studentsRows}
          topStickyOffset={64}
        />
      </TableContainer>
      {!!studentsNoLongerInInterventionRows.length && (
        <TableContainer>
          <InterventionCompletedTitle data-testid="INTRE_DEACTIVATED_STUDENTS">
            {t('reports.catch-up.studentsNoLongerInIntervention.title')}
          </InterventionCompletedTitle>
          <TableComponent
            id={'INTRE_DEATABLE'}
            columns={columnsForDeactivated}
            rows={studentsWithCompletedSessions}
            topStickyOffset={64}
          />
        </TableContainer>
      )}
    </>
  );
};
